import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Subscription, interval } from 'rxjs';
import { take } from 'rxjs/operators';
import { 
  InactivityDialogAction, 
  InactivityDialogResult, 
  InactivitySeverity,
  INACTIVITY_SEVERITY_THRESHOLDS
} from '../../../core/constants/inactivity.constants';

export interface InactivityWarningDialogData {
  secondsRemaining: number;
}

/**
 * Inactivity Warning Dialog Component
 * 
 * Displays a warning to the user before automatic logout due to inactivity.
 * Shows countdown timer and provides options to stay logged in or logout now.
 * 
 * HIPAA §164.312(a)(1)(iii) & 21 CFR Part 11 Compliance
 */
@Component({
  selector: 'app-inactivity-warning-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './inactivity-warning-dialog.component.html',
  styleUrls: ['./inactivity-warning-dialog.component.scss']
})
export class InactivityWarningDialogComponent implements OnInit, OnDestroy {
  secondsRemaining: number;
  private countdownSubscription?: Subscription;

  // Expose enum to template for type-safe comparisons
  readonly InactivitySeverity = InactivitySeverity;

  constructor(
    public dialogRef: MatDialogRef<InactivityWarningDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InactivityWarningDialogData
  ) {
    this.secondsRemaining = data.secondsRemaining;
    
    // Prevent closing dialog by clicking outside or pressing ESC
    this.dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    // Start countdown timer
    this.startCountdown();
  }

  ngOnDestroy(): void {
    this.countdownSubscription?.unsubscribe();
  }

  /**
   * Start countdown timer that updates every second
   */
  private startCountdown(): void {
    this.countdownSubscription = interval(1000)
      .pipe(take(this.secondsRemaining))
      .subscribe(() => {
        this.secondsRemaining--;
        
        // Auto-close dialog when countdown reaches 0
        if (this.secondsRemaining <= 0) {
          this.onLogoutNow();
        }
      });
  }

  /**
   * User clicked "Stay Logged In"
   * Resets the inactivity timer and closes dialog
   */
  onStayLoggedIn(): void {
    const result: InactivityDialogResult = { action: InactivityDialogAction.STAY };
    this.dialogRef.close(result);
  }

  /**
   * User clicked "Logout Now" or countdown reached 0
   * Triggers immediate logout
   */
  onLogoutNow(): void {
    const result: InactivityDialogResult = { action: InactivityDialogAction.LOGOUT };
    this.dialogRef.close(result);
  }

  /**
   * Format seconds into MM:SS format
   */
  get formattedTime(): string {
    const minutes = Math.floor(this.secondsRemaining / 60);
    const seconds = this.secondsRemaining % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Get warning severity class based on time remaining
   * Returns enum value for type-safe severity indication
   * 
   * Thresholds:
   * - CRITICAL: ≤ 5 seconds (Red)
   * - URGENT: ≤ 15 seconds (Orange)
   * - WARNING: > 15 seconds (Amber)
   */
  get severityClass(): InactivitySeverity {
    if (this.secondsRemaining <= INACTIVITY_SEVERITY_THRESHOLDS.CRITICAL) {
      return InactivitySeverity.CRITICAL;
    } else if (this.secondsRemaining <= INACTIVITY_SEVERITY_THRESHOLDS.URGENT) {
      return InactivitySeverity.URGENT;
    }
    return InactivitySeverity.WARNING;
  }
}
